<script lang="ts">
  import { onMount } from "svelte";
  import Swiper from "swiper/esm/svelte/swiper.svelte";
  import SwiperSlide from "swiper/esm/svelte/swiper-slide.svelte";
  import "swiper/swiper-bundle.min.css";
  import SwiperCore, { Lazy, Autoplay } from "swiper/core";
  import Picture from "$lib/components/Picture/Picture.svelte";
  import Typography from "$lib/components/Typography/Typography.svelte";
  import { Position, Alignment, Speed } from "$lib/gql/genCms/codegen";
  import type {
    CardConnection,
    CardEdge,
    DynamicGridCardContent,
    Maybe,
    Link as TLink,
  } from "$lib/gql/genCms/codegen";
  import Link from "../Link/Link.svelte";
  import inView from "$lib/utils/inView";
  import AutoScroll from "../AutoScroll/AutoScroll.svelte";

  SwiperCore.use([Lazy, Autoplay]);

  export let cards: CardEdge[] = [];
  export let autoscroll: boolean = false;
  export let className: string = "";

  $: cardContent = cards.map(
    card => card.node.content.content,
  ) as DynamicGridCardContent[];

  export let speed: Speed = Speed.Medium; // 'slow', 'medium', 'fast'

  let swiper: any;

  const getPositionClass = (position: Position): string => {
    switch (position) {
      case "LEFT":
        return "dynamic-grid__content--left";
      case "RIGHT":
        return "dynamic-grid__content--right";
      case "CENTER":
      default:
        return "dynamic-grid__content--center";
    }
  };

  const getAlignmentClass = (alignment: Alignment): string => {
    switch (alignment) {
      case "TOP":
        return "dynamic-grid__content--top";
      case "BOTTOM":
        return "dynamic-grid__content--bottom";
      case "CENTER":
      default:
        return "dynamic-grid__content--middle";
    }
  };

  const getTextAlignmentClass = (textAlignment?: Position): string => {
    if (!textAlignment) return "";
    switch (textAlignment) {
      case "LEFT":
        return "dynamic-grid__content-inner--text-left";
      case "RIGHT":
        return "dynamic-grid__content-inner--text-right";
      case "CENTER":
      default:
        return "dynamic-grid__content-inner--text-center";
    }
  };

  const onSwiper = (e: CustomEvent) => {
    const [swiperInstance] = e.detail;
    swiper = swiperInstance;
  };

  let isInView = true;

  let isMobile = false;
  let viewportWidth: number = 0;
  let mdWidth = 768;
  $: isMobile = viewportWidth < mdWidth;
  $: useSwiper = !autoscroll;

  let baseSpeed = isMobile ? 30 : 50;

  $: scrollSpeed = (() => {
    switch (speed) {
      case Speed.Slow:
        return baseSpeed * 0.5;
      case Speed.Medium:
        return baseSpeed;
      case Speed.Fast:
        return baseSpeed * 1.5;
      default:
        return baseSpeed;
    }
  })();
</script>

<style lang="scss">
  .fade-in {
    display: inline-block;
    animation: fadeIn 0.6s ease-out forwards;
    animation-delay: 0.2s;
    opacity: 0;
  }

  .fade-up {
    display: inline-block;
    animation: fadeUp 0.6s ease-out forwards;
    animation-delay: 0.6s;
  }
  .dynamic-grid {
    position: relative;
    width: 100%;
    overflow: hidden;

    &__swiper {
      width: 100%;
      height: 100%;
    }

    &__slide {
      height: auto;
    }

    &__card {
      position: relative;
      width: 100%;

      overflow: hidden;
      background-color: $inverse-surface;
      aspect-ratio: 4/5;
    }

    &__image-wrapper {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    &__image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &__content {
      position: absolute;
      z-index: 50;
      display: flex;
      padding: 2rem;
      width: 100%;
      height: 100%;

      @include breakpoint(md) {
        padding: 3rem;
      }

      @include breakpoint(lg) {
        padding: 4rem;
      }

      // Horizontal positioning
      &--left {
        justify-content: flex-start;
      }

      &--center {
        justify-content: center;
      }

      &--right {
        justify-content: flex-end;
      }

      // Vertical positioning
      &--top {
        align-items: flex-start;
      }

      &--middle {
        align-items: center;
      }

      &--bottom {
        align-items: flex-end;
      }
    }

    &__content-inner {
      display: flex;
      flex-direction: column;
      text-align: inherit;
      max-width: 100%;
      z-index: 50;
      gap: 0.25rem;

      @include breakpoint(md) {
        max-width: 70%;
      }

      // Text alignment classes
      &--text-left {
        text-align: left;
        align-items: flex-start;
      }

      &--text-center {
        text-align: center;
        align-items: center;
      }

      &--text-right {
        text-align: right;
        align-items: flex-end;
      }
    }

    &__title {
      margin: 0;
      color: $inverse-on-surface;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    &__body {
      margin: 0.5rem 0;
      color: $inverse-on-surface;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      word-wrap: break-word;
      overflow-wrap: break-word;
      white-space: normal;
    }

    &__cta {
      align-self: flex-start;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes fadeUp {
      from {
        transform: translateY(20px);
      }
      to {
        transform: translateY(0);
      }
    }

    // CSS Grid for desktop non-autoscroll
    &__scroll-container {
      display: flex;
      overflow-x: auto;
      scroll-behavior: smooth;
      -webkit-overflow-scrolling: touch;

      // Hide scrollbar
      scrollbar-width: none;
      -ms-overflow-style: none;
      &::-webkit-scrollbar {
        display: none;
      }
    }

    &__grid-item {
      flex: 0 0 auto;
    }
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

<div class="dynamic-grid {className}">
  {#if useSwiper}
    <Swiper
      on:swiper={e => onSwiper(e)}
      class="dynamic-grid__swiper"
      lazy={true}
      preload={true}
      direction="horizontal"
      loop={true}
      slidesPerView={1}
      breakpoints={{
        375: {
          slidesPerView: 1,
          slidesPerGroup: 1,
          spaceBetween: 0.5,
        },
        768: {
          slidesPerView: 2,
          slidesPerGroup: 2,
          spaceBetween: 0.5,
        },
        1280: {
          slidesPerView: 2,
          slidesPerGroup: 2,
          spaceBetween: 0.5,
        },
        1600: {
          slidesPerView: 2,
          slidesPerGroup: 2,
          spaceBetween: 0.5,
        },
      }}
    >
      {#each cardContent as card}
        <SwiperSlide class="dynamic-grid__slide">
          <div
            class="dynamic-grid__card"
            use:inView={{ threshold: 0.1 }}
            on:intersect={({ detail }) => {
              if (isInView) {
                return;
              }
              if (detail) {
                isInView = true;
              }
            }}
          >
            <div class="dynamic-grid__image-wrapper">
              <div
                class="dynamic-grid__content {getPositionClass(
                  card.position,
                )} {getAlignmentClass(card.alignment)}
            {isInView ? 'fade-up' : ''}
            "
              >
                <div
                  class="dynamic-grid__content-inner {getTextAlignmentClass(
                    card.textAlignment,
                  )}"
                >
                  <Typography
                    variant="h2"
                    theme={card.invert ? "inverse-on-surface" : "on-surface"}
                    ><span class="dynamic-grid__title">{card.title}</span
                    ></Typography
                  >
                  {#if card.body}
                    <span class="dynamic-grid__body">
                      <Typography
                        variant="body"
                        theme={card.invert
                          ? "inverse-on-surface"
                          : "on-surface"}
                      >
                        {@html card.body.html}
                      </Typography>
                    </span>
                  {/if}
                  {#if card.cta}
                    <Link
                      href={card.cta.href}
                      underline={false}
                      text={card.cta.text + " →"}
                      theme={card.invert ? "inverse-on-surface" : "on-surface"}
                    />
                  {/if}
                </div>
              </div>
              <Picture
                smImage={card.image?.uri}
                mdImage={card.image?.uri}
                altText={card.image?.altText || card.title}
              />
            </div>
          </div>
        </SwiperSlide>
      {/each}
    </Swiper>
  {:else}
    <AutoScroll
      speed={scrollSpeed}
      direction="left"
      gap={0}
      showPauseButton={autoscroll}
      pauseOnHover={true}
      itemsToShow={isMobile ? 1 : 2}
      class="dynamic-grid__scroll-container"
    >
      {#each cardContent as card}
        <div class="dynamic-grid__grid-item">
          <div
            class="dynamic-grid__card"
            use:inView={{ threshold: 0.1 }}
            on:intersect={({ detail }) => {
              if (isInView) {
                return;
              }
              if (detail) {
                isInView = true;
              }
            }}
          >
            <div class="dynamic-grid__image-wrapper">
              <div
                class="dynamic-grid__content {getPositionClass(
                  card.position,
                )} {getAlignmentClass(card.alignment)}
            {isInView ? 'fade-up' : ''}
            "
              >
                <div
                  class="dynamic-grid__content-inner {getTextAlignmentClass(
                    card.textAlignment,
                  )}"
                >
                  <Typography
                    variant="h2"
                    theme={card.invert ? "inverse-on-surface" : "on-surface"}
                    ><span class="dynamic-grid__title">{card.title}</span
                    ></Typography
                  >
                  {#if card.body}
                    <span class="dynamic-grid__body">
                      <Typography
                        variant="body"
                        theme={card.invert
                          ? "inverse-on-surface"
                          : "on-surface"}
                      >
                        {@html card.body.html}
                      </Typography>
                    </span>
                  {/if}
                  {#if card.cta}
                    <Link
                      href={card.cta.href}
                      text={card.cta.text + " →"}
                      underline={false}
                      theme={card.invert ? "inverse-on-surface" : "on-surface"}
                    />
                  {/if}
                </div>
              </div>
              <Picture
                smImage={card.image?.uri}
                mdImage={card.image?.uri}
                altText={card.image?.altText || card.title}
              />
            </div>
          </div>
        </div>
      {/each}
    </AutoScroll>
  {/if}
</div>
